import { Container } from '@design-system/container';
import { ExtendableImage } from '@design-system/extendable-image';
import { Section } from '@design-system/section';
import { Typography } from '@design-system/typography';
import { graphql, useStaticQuery } from 'gatsby';
import React, { ReactElement } from 'react';

import TMPLinkWrap from '~/components/TMPLinkWrap';
import { useCurrentLang } from '~/hooks/useCurrentLang';
import { Layout } from '~/layout/Layout';
import { imageMapper } from '~/mappers/image';
import { linkMapper } from '~/mappers/link/linkMapper';
import { typographyMapper } from '~/mappers/typography';

import { NotFoundItem, NotFoundQueryResult } from './models';

// eslint-disable-next-line arrow-body-style
const NotFoundPage = () => {
  const {
    currentLang: { locale: currentLangCode },
  } = useCurrentLang();
  const brand = process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string;

  const {
    allContentstackNotFound,
    allContentstackHeader,
    allContentstackL3HeaderExtension,
    allContentstackFooter,
  } = useStaticQuery<NotFoundQueryResult>(graphql`
    {
      allContentstackHeader {
        nodes {
          ...HeaderFragment
        }
      }
      allContentstackL3HeaderExtension {
        nodes {
          ...L3HeaderExtensionFragment
        }
      }
      allContentstackFooter {
        nodes {
          ...FooterFragment
        }
      }
      allContentstackNotFound {
        nodes {
          publishDetails: publish_details {
            locale
          }
          seo {
            ...SeoFragment
          }
          image {
            ...ImageFragment
          }
          button {
            ...LinkFragment
          }
          description {
            ...TypographyFragment
          }
          uid
          tags
          mode
          maxWidth: max_width
          removeAllSpacing: remove_all_spacing
        }
      }
    }
  `);

  const {
    publishDetails: { locale },
    uid,
    image,
    button,
    description,
    seo,
    maxWidth,
    mode,
    removeAllSpacing = true,
  } = allContentstackNotFound.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  ) as NotFoundItem;

  const header = allContentstackHeader.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const headerL3Extension = allContentstackL3HeaderExtension.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );

  const footer = allContentstackFooter.nodes.find(
    (item) =>
      item.publishDetails.locale === currentLangCode &&
      item.tags.includes(brand)
  );
  const mappedImage = imageMapper(image);

  return (
    <Layout
      {...{
        seo,
        header: header && {
          ...header,
          ...headerL3Extension,
        },
        footer,
        pageUid: uid,
        locale,
        brand,
        className: 'not-found',
      }}
    >
      <Section mode={mode} removeAllSpacing={removeAllSpacing}>
        <Container maxWidth={maxWidth} className="not-found__container">
          {mappedImage?.element ? (
            <ExtendableImage {...(mappedImage as { element: ReactElement })} />
          ) : null}
          <Typography
            {...typographyMapper(description)}
            className="not-found__description"
          />
          <TMPLinkWrap {...linkMapper(button)} className="not-found__link" />
        </Container>
      </Section>
    </Layout>
  );
};

export default NotFoundPage;
